<template>
  <projector-header @toggle-info="toggleInfoPanel" />
  <info-panel
    :open="infoPanelOpen"
    :title="topMostDocument?.displayTitle"
    @close="infoPanelOpen = false"
  >
    <template v-if="topMostDocument">
      <div class="doc-meta">
        <img
          v-if="topMostDocument.thumbnailDownloadUrl"
          :src="getStorageProxyUrl(topMostDocument.thumbnailDownloadUrl)"
          :alt="topMostDocument.displayTitle"
          class="doc-meta__thumbnail"
        />

        <div class="doc-meta__rows">
          <div class="doc-meta__row" v-if="topMostDocument.displayDate">
            <span class="doc-meta__label">Datum:</span>
            <span>{{ metaDate }}</span>
          </div>
          <div class="doc-meta__row">
            <template v-if="metaIsVideo">
              <span class="doc-meta__label">Dauer:</span>
              <span>{{ metaDuration }}</span>
            </template>
            <template v-else>
              <span class="doc-meta__label">Seiten:</span>
              <span>{{ topMostDocument.pages }}</span>
            </template>
          </div>
          <div class="doc-meta__row">
            <span class="doc-meta__label">Typ:</span>
            <span>{{ metaType }}</span>
          </div>
          <div class="doc-meta__row" v-if="topMostDocument.fileSize">
            <span class="doc-meta__label">Größe:</span>
            <span>{{ metaSize }}</span>
          </div>
        </div>

        <div class="doc-meta__tags" v-if="metaTags.length > 0">
          <span class="doc-meta__label">Tags:</span>
          <div class="doc-meta__tag-list">
            <span class="doc-meta__tag" v-for="tag in metaTags" :key="tag">{{
              tag
            }}</span>
          </div>
        </div>

        <div class="doc-meta__analytics" v-if="canViewPresentationAnalytics">
          <span class="doc-meta__label">Analytics:</span>
          <div class="doc-meta__analytics-links">
            <router-link
              class="doc-meta__analytics-link"
              :to="performanceAnalyticsLink"
              data-test="projector-analytics-link-performance"
            >
              Performance over time
            </router-link>
            <router-link
              class="doc-meta__analytics-link"
              :to="pageAnalyticsLink"
              data-test="projector-analytics-link-pages"
            >
              Page performance
            </router-link>
          </div>
        </div>
      </div>
    </template>
  </info-panel>
  <div class="stacked-projector">
    <projector-button style="z-index: 100" />
    <div ref="stack">
      <div
        v-if="currentTopMostFirestoreId"
        class="presenter"
        :key="currentTopMostStackKey ?? undefined"
        :style="{ 'z-index': documentIds.length }"
      >
        <document-presenter
          v-if="validatedTopMostStackKey === currentTopMostStackKey"
          :document-id="currentTopMostFirestoreId"
          :validated-document="topMostDocument"
          v-model:page="pageNumbers[documentIds.length - 1]"
          :fetching="fetching && !topMostDocument"
          :retry-disabled="
            fetching || topMostDocumentStale || topMostDocumentHasNext
          "
          :zoom="projectorIsEnabled ? zoomInPresentation : null"
          :laserpointer="projectorIsEnabled ? laserpointerInPresentation : null"
          :user-controlled="!!projectorIsEnabled"
          @update:zoom="
            (value) => handleZoomUpdate(value, documentIds.length - 1)
          "
          @update:laserpointer="
            (value) => handleLaserpointerUpdate(value, documentIds.length - 1)
          "
          @go-to-document="(id) => goToDocument(id, documentIds.length - 1)"
          @retry="retryTopMostDocument"
        >
          <template #fullscreenButton>
            <button class="button" @click="toggleFullscreen()">
              <tooltip v-if="isFullscreen">
                <template v-slot>
                  <span class="material-symbols-outlined"
                    >close_fullscreen</span
                  >
                </template>
                <template #tooltip>{{
                  $t("documentPresenter.controls.closeFullscreen")
                }}</template>
              </tooltip>
              <tooltip v-else>
                <template v-slot>
                  <span class="material-symbols-outlined">fullscreen</span>
                </template>
                <template #tooltip>{{
                  $t("documentPresenter.controls.openFullscreen")
                }}</template>
              </tooltip>
            </button>
          </template>
          <template #buttonsAtTop="{ pageIsPopover }">
            <button class="button" @click="goBack" v-if="!pageIsPopover">
              <span class="material-symbols-outlined">arrow_back</span>
            </button>
          </template>
        </document-presenter>
        <div
          v-else
          class="stacked-projector__status"
          data-test="stacked-projector-access-loading"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {{ $t("documentPresenter.loading") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
let nextStackedProjectorInstanceId = 0;
</script>

<script lang="ts" setup>
// https://github.com/Pitchview/Projector_iOS/blob/developer/slides/helper/AudienceStatusTracker.swift
import { computed, watch, ref, onUnmounted } from "vue";
import { useFullscreen } from "@vueuse/core";
import { useStore } from "vuex";

import { DocumentPresenter, Tooltip, ProjectorButton } from "@/components";
import { useProjector, getStorageProxyUrl, Formatter, MimeType } from "@/util";
import type { LaserPointer, Rect } from "@/models";
import {
  type GetDocumentByFirestoreIdQuery,
  useGetDocumentByFirestoreIdQuery,
} from "@/graphql/operations";
import { useRouter, useRoute, LocationQuery } from "vue-router";
import { useDocumentStore } from "@/store/document";
import ProjectorHeader from "./ProjectorHeader.vue";
import InfoPanel from "./InfoPanel.vue";
import { hasPresentationAnalyticsAccess } from "../document_editor/presentation_analytics/presentationAnalytics";

const router = useRouter();
const route = useRoute();
const documentStore = useDocumentStore();
const store = useStore();

const infoPanelOpen = ref(false);
const toggleInfoPanel = () => {
  infoPanelOpen.value = !infoPanelOpen.value;
};

const goBack = () => {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push({
      name: "Workspace",
      params: { workspaceId: route.params.workspaceId as string },
    });
  }
};

const goToDocument = (id: string, index: number) => {
  router.push({ ...route, query: { fid: [...documentIds.value, id] } });
};

const handleZoomUpdate = (value: Rect, index: number) => {
  if (!projectorIsEnabled.value) return;
  if (index !== documentIds.value.length - 1) return;
  zoomInPresentation.value = value;
};

const handleLaserpointerUpdate = (value: LaserPointer, index: number) => {
  if (!projectorIsEnabled.value) return;
  if (index !== documentIds.value.length - 1) return;
  laserpointerInPresentation.value = value;
};

const documentIds = ref<string[]>([]);
// get the current document ids
const setDocumentIds = (query: LocationQuery) => {
  const fid = query.fid;
  documentIds.value =
    fid === null
      ? []
      : typeof fid === "string"
        ? [fid]
        : ([...fid] as string[]);
};

setDocumentIds(route.query);
// Set initial page from query param (e.g. from search result page hit)
const initialPage = route.query.page ? Number(route.query.page) : undefined;

watch(route, (newRoute) => {
  setDocumentIds(newRoute.query);
  // Jump to page if query param changed (e.g. from search page hit)
  if (newRoute.query.page) {
    const p = Number(newRoute.query.page);
    if (p > 0 && pageNumbers.value.length > 0) {
      pageNumbers.value[pageNumbers.value.length - 1] = p;
    }
  }
});

let stack = ref<HTMLDivElement | null>(null);
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(stack);

let pageNumbers = ref<number[]>(initialPage ? [initialPage] : []);
let documents = ref<Document[]>([]);

watch(documentIds, (newArray) => {
  pageNumbers.value = pageNumbers.value.slice(0, newArray.length);
  documents.value = documents.value.slice(0, newArray.length);
});

// get the top-most document
// -------------------------
const currentTopMostFirestoreId = computed(
  () => documentIds.value[documentIds.value.length - 1],
);
const currentTopMostStackKey = computed(() => {
  const firestoreId = currentTopMostFirestoreId.value;

  return firestoreId ? `${documentIds.value.length - 1}:${firestoreId}` : null;
});

const {
  data: topMostDocumentResult,
  error: topMostDocumentError = ref(undefined),
  executeQuery,
  fetching,
  hasNext: topMostDocumentHasNext = ref(false),
  operation: topMostDocumentOperation,
  stale: topMostDocumentStale,
} = useGetDocumentByFirestoreIdQuery({
  variables: computed(() => ({
    id: currentTopMostFirestoreId.value,
  })),
  // Every parent access check is started explicitly with a request-generation
  // marker so an older same-key URQL result cannot authorize a later stack.
  pause: true,
  requestPolicy: "cache-and-network",
});

const validatedTopMostStackKey = ref<string | null>(null);
const validatedTopMostDocument =
  ref<GetDocumentByFirestoreIdQuery["document"]>(null);
const activeTopMostRequestStackKey = ref<string | null>(null);
const activeTopMostAccessRequestId = ref<number | null>(null);
const pendingTopMostRequestStackKey = ref<string | null>(null);
const pendingTopMostRequestFirestoreId = ref<string | null>(null);
let nextTopMostAccessRequestId = 0;
let retriedMismatchedTopMostAccessRequestId: number | null = null;
let pendingTopMostRequestStartScheduled = false;
const stackedProjectorInstanceId = ++nextStackedProjectorInstanceId;
const topMostAccessRequestSourcePrefix = `StackedProjector:${stackedProjectorInstanceId}:`;

function getTopMostAccessRequestId(source: string | undefined): number | null {
  if (!source?.startsWith(topMostAccessRequestSourcePrefix)) return null;

  const requestId = Number(
    source.slice(topMostAccessRequestSourcePrefix.length),
  );
  return Number.isSafeInteger(requestId) ? requestId : null;
}

function executeTopMostAccessRequest(requestId: number) {
  executeQuery({
    requestPolicy: "network-only",
    meta: { source: `${topMostAccessRequestSourcePrefix}${requestId}` },
  });
}

function startTopMostAccessRequest(stackKey: string, firestoreId: string) {
  if (
    currentTopMostStackKey.value !== stackKey ||
    currentTopMostFirestoreId.value !== firestoreId
  ) {
    return;
  }

  pendingTopMostRequestStackKey.value = null;
  pendingTopMostRequestFirestoreId.value = null;
  activeTopMostRequestStackKey.value = stackKey;
  activeTopMostAccessRequestId.value = ++nextTopMostAccessRequestId;
  retriedMismatchedTopMostAccessRequestId = null;
  executeTopMostAccessRequest(activeTopMostAccessRequestId.value);
}

function retryTopMostDocument() {
  const stackKey = currentTopMostStackKey.value;
  const firestoreId = currentTopMostFirestoreId.value;
  if (
    !stackKey ||
    !firestoreId ||
    fetching.value ||
    topMostDocumentStale.value ||
    topMostDocumentHasNext.value
  ) {
    return;
  }

  startTopMostAccessRequest(stackKey, firestoreId);
}

function schedulePendingTopMostAccessRequest() {
  if (pendingTopMostRequestStartScheduled) return;
  pendingTopMostRequestStartScheduled = true;

  queueMicrotask(() => {
    pendingTopMostRequestStartScheduled = false;

    const stackKey = pendingTopMostRequestStackKey.value;
    const firestoreId = pendingTopMostRequestFirestoreId.value;
    if (
      !stackKey ||
      !firestoreId ||
      fetching.value ||
      topMostDocumentStale.value ||
      topMostDocumentHasNext.value
    ) {
      return;
    }

    startTopMostAccessRequest(stackKey, firestoreId);
  });
}

watch(
  [currentTopMostStackKey, currentTopMostFirestoreId],
  ([stackKey, firestoreId], [, oldFirestoreId]) => {
    validatedTopMostStackKey.value = null;
    validatedTopMostDocument.value = null;
    activeTopMostRequestStackKey.value = null;
    activeTopMostAccessRequestId.value = null;
    retriedMismatchedTopMostAccessRequestId = null;
    pendingTopMostRequestStackKey.value = stackKey;
    pendingTopMostRequestFirestoreId.value = firestoreId ?? null;

    if (!stackKey || !firestoreId) return;

    if (
      firestoreId === oldFirestoreId &&
      (fetching.value ||
        topMostDocumentStale.value ||
        topMostDocumentHasNext.value)
    ) {
      return;
    }

    startTopMostAccessRequest(stackKey, firestoreId);
  },
  { immediate: true, flush: "sync" },
);

watch(
  [
    currentTopMostStackKey,
    currentTopMostFirestoreId,
    fetching,
    topMostDocumentStale,
    topMostDocumentError,
    topMostDocumentHasNext,
    topMostDocumentOperation,
    topMostDocumentResult,
  ],
  ([
    requestedStackKey,
    requestedId,
    isFetching,
    isStale,
    queryError,
    hasNext,
    operation,
    result,
  ]) => {
    if (!requestedStackKey || !requestedId) {
      return;
    }

    if (pendingTopMostRequestStackKey.value === requestedStackKey) {
      if (!isFetching && !isStale && !hasNext) {
        schedulePendingTopMostAccessRequest();
      }
      return;
    }

    if (activeTopMostRequestStackKey.value !== requestedStackKey) return;

    const operationId = operation?.variables?.id;
    const activeRequestId = activeTopMostAccessRequestId.value;
    const resultRequestId = getTopMostAccessRequestId(
      operation?.context?.meta?.source,
    );

    if (isFetching || activeRequestId === null) {
      return;
    }

    if (
      !isStale &&
      !queryError &&
      !hasNext &&
      result !== undefined &&
      operationId === requestedId &&
      resultRequestId === activeRequestId
    ) {
      validatedTopMostDocument.value = result?.document ?? null;
      validatedTopMostStackKey.value = requestedStackKey;
      activeTopMostAccessRequestId.value = null;
      retriedMismatchedTopMostAccessRequestId = null;
      return;
    }

    if (
      !isStale &&
      !hasNext &&
      result !== undefined &&
      operationId === requestedId &&
      resultRequestId !== activeRequestId &&
      retriedMismatchedTopMostAccessRequestId !== activeRequestId
    ) {
      retriedMismatchedTopMostAccessRequestId = activeRequestId;
      queueMicrotask(() => {
        if (
          activeTopMostRequestStackKey.value === requestedStackKey &&
          activeTopMostAccessRequestId.value === activeRequestId &&
          !fetching.value &&
          !topMostDocumentStale.value &&
          !topMostDocumentHasNext.value
        ) {
          executeTopMostAccessRequest(activeRequestId);
        }
      });
    }
  },
  { immediate: true, flush: "post" },
);

// synchronize the top-most document with the projector, if active
// ---------------------------------------------------------------
const {
  document: documentInPresentation,
  page: pageInPresentation,
  zoom: zoomInPresentation,
  laserpointer: laserpointerInPresentation,
  isEnabled: projectorIsEnabled,
} = useProjector();

const topMostDocument = computed(() => {
  if (validatedTopMostStackKey.value !== currentTopMostStackKey.value) {
    return null;
  }

  const document = validatedTopMostDocument.value;

  return document?.deletedAt === null &&
    document.firestoreId === currentTopMostFirestoreId.value
    ? document
    : null;
});

const topMostDocumentWorkspaceId = computed(
  () => topMostDocument.value?.workspace?.id ?? null,
);

// document metadata computeds
const metaDate = computed(() => {
  const d = topMostDocument.value?.displayDate;
  return d ? Formatter.formatDate(new Date(d), "%Y-%m-%d %H:%M") : "";
});
const metaIsVideo = computed(() =>
  topMostDocument.value
    ? MimeType.isVideo(topMostDocument.value.mimeType)
    : false,
);
const metaType = computed(() => {
  const doc = topMostDocument.value;
  if (!doc) return "";
  if (doc.originalMimeType) return MimeType.getName(doc.originalMimeType);
  return MimeType.getName(doc.mimeType);
});
const metaSize = computed(() => {
  const s = topMostDocument.value?.fileSize;
  return s ? Formatter.formatSize(parseInt(s, 10)) : "";
});
const metaDuration = computed(() => {
  const p = topMostDocument.value?.pages;
  return p != null ? Formatter.formatDuration(p) : "";
});
const metaTags = computed<string[]>(
  () => (topMostDocument.value?.tags as string[] | null) ?? [],
);

const canViewPresentationAnalytics = computed(() =>
  hasPresentationAnalyticsAccess({
    hasCurrentAccessResult: true,
    hasPresentationAnalytics: store.getters.user.hasPresentationAnalytics(),
    mayAdministrateRoot: Boolean(
      topMostDocument.value?.rootWorkspace?.myPrivileges?.mayAdministrate,
    ),
    deletedAt: topMostDocument.value?.deletedAt,
    mimeType: topMostDocument.value?.mimeType,
    numNormalPages: topMostDocument.value?.numNormalPages,
  }),
);

const performanceAnalyticsLink = computed(() => {
  if (!topMostDocument.value?.id || !topMostDocumentWorkspaceId.value) {
    return "";
  }

  return router.resolve({
    name: "DocumentPresentationAnalytics",
    params: {
      workspaceId: topMostDocumentWorkspaceId.value,
      documentId: topMostDocument.value.id,
    },
    query: {
      view: "performance",
      preset: "90d",
    },
  }).fullPath;
});

const pageAnalyticsLink = computed(() => {
  if (!topMostDocument.value?.id || !topMostDocumentWorkspaceId.value) {
    return "";
  }

  return router.resolve({
    name: "DocumentPresentationAnalytics",
    params: {
      workspaceId: topMostDocumentWorkspaceId.value,
      documentId: topMostDocument.value.id,
    },
    query: {
      view: "pages",
      preset: "90d",
    },
  }).fullPath;
});

watch(
  topMostDocument,
  (newTopMostDocument, oldTopMostDocument) => {
    documentStore.set(
      newTopMostDocument?.id ?? null,
      newTopMostDocument?.doesSupportSharedspace ?? false,
    );

    //document did not change
    if (
      JSON.stringify(newTopMostDocument) ===
      JSON.stringify(documentInPresentation.value)
    ) {
      return;
    }

    documentInPresentation.value = newTopMostDocument ?? undefined;
  },
  { immediate: true },
);

const intervalId = window.setInterval(() => {
  if (
    !currentTopMostStackKey.value ||
    !currentTopMostFirestoreId.value ||
    fetching.value ||
    topMostDocumentStale.value ||
    topMostDocumentHasNext.value
  ) {
    return;
  }

  startTopMostAccessRequest(
    currentTopMostStackKey.value,
    currentTopMostFirestoreId.value,
  );
}, 5000);

watch(
  computed(() => pageNumbers.value[pageNumbers.value.length - 1]),
  (newValue, oldValue) => {
    pageInPresentation.value = newValue;
  },
  { immediate: true },
);

onUnmounted(() => {
  documentStore.set(null, false);
  documentInPresentation.value = undefined;
  window.clearInterval(intervalId);
});
</script>

<style scoped>
.stacked-projector {
  position: relative;
  width: calc(100% - 3em);
  height: calc(100vh - 65px);
  margin-left: 1.5em;
  margin-right: 1.5em;
}

.presenter {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
}

.presenter button span {
  margin-right: 0;
}

.stacked-projector__status {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  box-sizing: border-box;
  text-align: center;
  background-color: var(--classic-page, #fafafa);
}

/* Document metadata in InfoPanel */
.doc-meta {
  font-size: 0.9rem;
}

.doc-meta__thumbnail {
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  border-radius: 6px;
  background: #fafafa;
  margin-bottom: 1.2em;
}

.doc-meta__rows {
  border-top: 1px solid var(--classic-border-subtle, #e8e8e8);
  padding-top: 0.8em;
}

.doc-meta__row {
  margin-bottom: 0.4em;
}

.doc-meta__label {
  color: var(--classic-text-secondary, rgba(82, 74, 62, 0.82));
  display: inline-block;
  width: 80px;
}

.doc-meta__tags {
  border-top: 1px solid var(--classic-border-subtle, #e8e8e8);
  padding-top: 0.8em;
  margin-top: 0.8em;
}

.doc-meta__tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4em;
  margin-top: 0.4em;
}

.doc-meta__tag {
  background: var(--classic-tag-surface, #f0f0f0);
  color: var(--classic-text, #444);
  padding: 0.15em 0.6em;
  border-radius: 4px;
  font-size: 0.85em;
}

.doc-meta__analytics {
  border-top: 1px solid var(--classic-border-subtle, #e8e8e8);
  padding-top: 0.8em;
  margin-top: 0.8em;
}

.doc-meta__analytics-links {
  display: flex;
  flex-direction: column;
  gap: 0.6em;
  margin-top: 0.5em;
}

.doc-meta__analytics-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid var(--classic-border, #d6e5e7);
  background: var(--classic-surface, #f8fbfb);
  color: var(--classic-accent, #25506f);
  padding: 0.7em 0.9em;
  text-decoration: none;
  font-weight: 500;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    color 0.15s ease;
}

.doc-meta__analytics-link:hover {
  border-color: var(--classic-accent-hover, #35b6b9);
  background: var(--classic-accent-soft, #edf8f8);
  color: var(--classic-accent-hover, #004d50);
}
</style>
